// Network config, overridable via env vars (see .env.example) — must be
// NEXT_PUBLIC_* since this is read from client components too (providers/wagmi.ts).
export const RPC_URL = process.env.NEXT_PUBLIC_RPC_URL || "https://rpc.dotone.online";
export const CHAIN_ID = process.env.NEXT_PUBLIC_CHAIN_ID ? Number(process.env.NEXT_PUBLIC_CHAIN_ID) : 505;
const EXPLORER_URL_PRIMARY = process.env.NEXT_PUBLIC_EXPLORER_URL || "https://dev.dotscan.one";
const EXPLORER_URL_FALLBACK = "https://dotscan.one";

let activeExplorerUrl = EXPLORER_URL_PRIMARY;
let explorerUrlReady: Promise<string> | null = null;

async function isReachable(url: string, timeoutMs = 4000): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const res = await fetch(url, { method: "HEAD", signal: controller.signal, cache: "no-store" });
    clearTimeout(timer);
    return res.ok;
  } catch {
    return false;
  }
}

/** Resolves to the primary explorer URL if it's reachable, otherwise falls
 * back to the public dotscan.one instance. Runs the reachability check once
 * per process/session and caches the result. */
export function resolveExplorerUrl(): Promise<string> {
  if (!explorerUrlReady) {
    explorerUrlReady = isReachable(EXPLORER_URL_PRIMARY).then((ok) => {
      activeExplorerUrl = ok ? EXPLORER_URL_PRIMARY : EXPLORER_URL_FALLBACK;
      return activeExplorerUrl;
    });
  }
  return explorerUrlReady;
}

/** Best-effort synchronous read for UI code that can't await — returns the
 * primary URL until resolveExplorerUrl() has settled at least once. */
export function getExplorerUrl(): string {
  return activeExplorerUrl;
}

// Kick off the reachability check immediately so it's usually resolved by
// the time UI code needs a link.
void resolveExplorerUrl();

// Google Analytics (GA4) measurement ID — set in .env, not defaulted here.
// Empty disables the tag entirely (see app/layout.tsx) rather than loading
// gtag.js pointed at nothing.
export const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

// Canonical production URL — used for metadataBase, OG/Twitter tags,
// robots.txt and sitemap.xml. Override in production; localhost is fine for dev.
export const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || "http://localhost:3000";

// WalletConnect Cloud (cloud.reown.com) project ID — powers Reown AppKit's
// modal (providers/wagmi.ts), which is what lets a plain mobile browser
// connect to a wallet app via QR/deep-link. Omitted entirely when unset.
export const WALLETCONNECT_PROJECT_ID = process.env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID;

// BNB Chain (BSC) swaps route through LI.FI (data/lifi.ts) instead of our
// own AMM — we have no router/liquidity deployed on BSC, see the trading-view
// branch discussion. Public RPC unless overridden.
export const BSC_RPC_URL = process.env.NEXT_PUBLIC_BSC_RPC_URL || "https://bsc-dataseed.binance.org";

// Same LI.FI-routed pattern as BSC above (see config/networks.ts) — public
// RPCs unless overridden.
export const ETH_RPC_URL = process.env.NEXT_PUBLIC_ETH_RPC_URL || "https://eth.llamarpc.com";
export const POLYGON_RPC_URL = process.env.NEXT_PUBLIC_POLYGON_RPC_URL || "https://polygon-rpc.com";
export const ARBITRUM_RPC_URL = process.env.NEXT_PUBLIC_ARBITRUM_RPC_URL || "https://arb1.arbitrum.io/rpc";

// Identifies us to LI.FI's API (required by every request) and determines
// where our cut of each BSC swap's fee gets paid out — tied to the payout
// wallet registered for this integrator string at https://portal.li.fi.
export const LIFI_INTEGRATOR = process.env.NEXT_PUBLIC_LIFI_INTEGRATOR || "dot-swap";

// LI.FI API key from https://portal.li.fi — raises rate limits over the
// unauthenticated default. Separate from fee collection: registering an
// integrator string with a payout wallet (see LIFI_INTEGRATOR above) is
// still required before `fee` is accepted on a quote request.
export const LIFI_API_KEY = process.env.NEXT_PUBLIC_LIFI_API_KEY;

// Our fee on top of LI.FI's own ~25bps default, as a fraction (0.01 = 1%).
// Deducted from the input amount and paid to the integrator's registered
// wallet automatically — see LI.FI's fee-collection docs.
export const LIFI_FEE = process.env.NEXT_PUBLIC_LIFI_FEE ? Number(process.env.NEXT_PUBLIC_LIFI_FEE) : 0.001;
